import React, { useState, useEffect } from "react";
import clsx from "clsx";
import styles from "./TrancheApproval.module.css";
import { LuExternalLink, LuThumbsUp, LuThumbsDown } from "react-icons/lu";
import ActivityCard from "./ActivityCard";
import {
  approvedTranch,
  rejectTranch,
  getTrancheByIdV2,
} from "../../service/api";
import { showNotification } from "../../service/showNotification";
import { FiCheckCircle } from "react-icons/fi";
import { useHistory, useParams } from "react-router-dom";
import RejectTrancheModal from "./RejectTrancheModal";
import { TrancheBotV2Status } from "../../utils/constant";

const TrancheApproval = () => {
  const history = useHistory();
  const { trancheId } = useParams();
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [loading, setLoading] = useState("");
  const [tranche, setTranche] = useState(null);

  const fetchTranche = async () => {
    try {
      setLoading("fetch_tranche");
      const res = await getTrancheByIdV2(trancheId);
      if (res?.data?.docs?.length > 0) {
        setTranche(res.data.docs[0]);
      }
    } catch (error) {
      console.error("Error fetching tranche:", error);
    } finally {
      setLoading("");
    }
  };

  // Fetch tranche details
  useEffect(() => {
    if (trancheId) fetchTranche();
  }, [trancheId]);

  const handleApprovedTranche = async (id) => {
    try {
      setLoading("approve");
      await approvedTranch(id);
      showNotification("success", "Tranche approved successfully!");
      fetchTranche();
    } catch (error) {
      console.error("Error approving tranche:", error);
      console.log(error?.response?.data?.message);
      const isZohoFailure =
        error?.response?.data?.message?.toLowerCase().includes("zoho failed") ||
        error?.message?.toLowerCase().includes("zoho failed");

      if (isZohoFailure) {
        showNotification(
          "error",
          "Zoho failed — Redirecting to Tranche Bot v2.."
        );
        setTimeout(() => {
          window.location.href = "/#/tranche-bot-v2";
        }, 1500);
      } else {
        showNotification("error", "Failed to approve tranche.");
      }
    } finally {
      setLoading("");
    }
  };

  const handleRejectTranche = async (id, reason) => {
    try {
      setLoading("reject");
      await rejectTranch(id, { message: reason });
      showNotification("success", "Tranche rejected successfully!");
      fetchTranche();
    } catch (error) {
      console.error("Error rejecting tranche:", error);
      showNotification("error", "Failed to reject tranche.");
    } finally {
      setLoading("");
    }
  };

  const onClickReject = () => {
    handleRejectTranche(trancheId, rejectReason);
    setRejectModalOpen(false);
    setRejectReason("");
  };

  // redirect to asset v2 page
  const handleAssetClick = (asset) => {
    history.push({
      pathname: "/assets-v2",
      state: {
        id: asset?.assetId,
      },
    });
  };

  const redirectToTrancheBotPage = () => {
    history.replace({
      pathname: "/tranche-bot-v2",
    });
  };

  // loaders
  const trancheLoader = "fetch_tranche" === loading,
    approveLoader = "approve" === loading,
    rejectLoader = "reject" === loading;

  // status
  const isApproved = tranche?.status === TrancheBotV2Status.SUCCESS;
  const isRejected = tranche?.status === TrancheBotV2Status.REJECTED;
  const isZohoFailed = tranche?.status === TrancheBotV2Status.ZOHO_FAILED;
  const canBeApprovedOrRejected =
    tranche?.status === TrancheBotV2Status.SENT_FOR_APPROVAL;

  if (trancheLoader && !tranche) {
    return <p className="text-center mt-10">Loading tranche details...</p>;
  }

  if (!tranche) {
    return <p className="text-center mt-10">No tranche found.</p>;
  }

  return (
    <>
      <div className={styles.pageContainer}>
        <main className={styles.mainGrid}>
          {/* Left Column: Approval Request Card */}
          <div className={styles.leftColumn}>
            {!isApproved && (
              <>
                <div className={styles.header}>
                  <h1 className={styles.title}>Approval Request</h1>
                  <p className={styles.subtitle}>
                    Review the following tranche for {tranche?.contactGroup}.
                  </p>
                </div>
                <div className={styles.card}>
                  <div>
                    <p className={styles.label}>Tranche Submitted</p>
                    <span className={styles.trancheTitleLink}>
                      {tranche?.trancheName}
                      {/* <LuExternalLink size={20} /> */}
                    </span>
                  </div>

                  <hr className={styles.divider} />

                  <div>
                    <h2 className={styles.sectionTitle}>Linked Assets</h2>
                    <ul className={styles.list}>
                      {tranche?.assetLocksV2?.map((asset) => (
                        <li
                          key={asset._id}
                          className={styles.listItem}
                          onClick={() => handleAssetClick(asset)}
                        >
                          <span className={styles.assetName}>
                            {asset.dealSource} ({asset.state || ""}) - $
                            {asset.price}
                          </span>
                          {/* <LuExternalLink size={18} /> */}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <hr className={styles.divider} />

                  <div>
                    <h2 className={styles.sectionTitle}>Final Summary</h2>
                    <div className={styles.summaryList}>
                      <div className={styles.summaryItem}>
                        <p>Total Equity:</p>
                        <p className={styles.summaryValue}>
                          ${tranche?.totalPurchaseAmount?.toLocaleString()}
                        </p>
                      </div>
                      <div className={styles.summaryItem}>
                        <p>ITC %:</p>
                        <p className={styles.summaryValue}>
                          {tranche?.totalItcPercent}%
                        </p>
                      </div>
                      <div className={styles.summaryItem}>
                        <p>EC To Deploy:</p>
                        <p
                          className={clsx(
                            styles.summaryValue,
                            styles.dangerText
                          )}
                        >
                          ${tranche?.ecToDeploy?.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Status-based rendering */}
            {isApproved && (
              <div className={styles.approvedTranche}>
                <FiCheckCircle className={styles.approveTrancheIcon} />
                <h1 className={styles.title}>Tranche Approved</h1>
                <p className={styles.subtitle}>
                  Tranche{" "}
                  <span className={styles.highlightText}>
                    {tranche?.trancheName}
                  </span>{" "}
                  is now locked and Bills of Sale has been generated.
                </p>
                <button
                  className={styles.startNewSearch}
                  onClick={redirectToTrancheBotPage}
                >
                  Start New Search
                </button>
              </div>
            )}

            {isRejected && (
              <div className={styles.rejectedTranche}>
                <p className={styles.subtitle}>
                  Tranche{" "}
                  <span className={styles.highlightText}>
                    {tranche?.trancheName}
                  </span>{" "}
                  was rejected.
                </p>
              </div>
            )}

            {isZohoFailed && (
              <div className={styles.zohoFailedTranche}>
                <p className={styles.subtitle}>
                  Tranche{" "}
                  <span className={styles.highlightText}>
                    {tranche?.trancheName}
                  </span>{" "}
                  is failed in Zoho.
                </p>
              </div>
            )}

            {canBeApprovedOrRejected && (
              <>
                <div className={styles.buttonGroup}>
                  <button
                    className={clsx(styles.actionButton, styles.rejectButton)}
                    onClick={() => setRejectModalOpen(true)}
                    disabled={rejectLoader}
                  >
                    <LuThumbsDown /> {rejectLoader ? "Rejecting..." : "Reject"}
                  </button>
                  <button
                    className={clsx(styles.actionButton, styles.approveButton)}
                    onClick={() => handleApprovedTranche(trancheId)}
                    disabled={approveLoader}
                  >
                    <LuThumbsUp />
                    {approveLoader ? "Approving..." : "Approve Tranche"}
                  </button>
                </div>
              </>
            )}
          </div>

          <ActivityCard />
        </main>
      </div>

      <RejectTrancheModal
        isOpen={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        rejectReason={rejectReason}
        setRejectReason={setRejectReason}
        onReject={onClickReject}
        loading={loading}
      />
    </>
  );
};

export default TrancheApproval;
